import {
    groupAssigneeNames,
    groupMemberResponsibilities
} from "../model/taskManagement/TaskData"
import {
    instantiateTaskIssues,
    instantiateTaskPullRequest
} from "../model/taskManagement/TaskFactory"
import {
    githubGraphQlEndpoint,
    githubGraphQlQueryOptions
} from "../model/GithubGraphQL"

/**
 * Parse raw group org project task management data fetched from GitHub GraphQL API for visualizing weekly report format
 * Processes data into formatted TaskIssue instances, and TaskPullRequest instances for appending to TaskIssue instances
 * @param {Object} rawGitHubOrgProjectData raw group org project task management data fetched from GitHub GraphQL API
 * @returns {*[TaskIssue]} array of TaskIssue objects containing associated TaskPullRequest instance objects as variable
 */
const getFormattedGitHubOrgProjectTaskData = (rawGitHubOrgProjectData) => {
    const
        taskIssues = [],
        taskPullRequests = [],
        rawTasksData = rawGitHubOrgProjectData["data"]["organization"]["projectV2"]["items"]["nodes"];
    let
        kanbanStatus,
        assignees;

    for (let rawTaskData in rawTasksData) {
        for (let kanbanStatusData in rawTasksData[rawTaskData]["fieldValues"]["nodes"]) {
            if ("name" in rawTasksData[rawTaskData]["fieldValues"]["nodes"][kanbanStatusData]) {
                kanbanStatus = rawTasksData[rawTaskData]["fieldValues"]["nodes"][kanbanStatusData]["name"];
                break;
            }
        }

        // Skip retrieving Issue data for draft Issues
        if (typeof rawTasksData[rawTaskData]["content"]["assignees"] == 'undefined') {
            continue;
        }

        assignees = [];
        for (let assigneeData in rawTasksData[rawTaskData]["content"]["assignees"]["nodes"]) {
            assignees.push(rawTasksData[rawTaskData]["content"]["assignees"]["nodes"][assigneeData]["login"]);
        }

        if (rawTasksData[rawTaskData]["content"]["__typename"] === "Issue") {
            taskIssues.push(...instantiateTaskIssues(rawTasksData[rawTaskData], kanbanStatus, assignees));
        } else {
            taskPullRequests.push(instantiateTaskPullRequest(rawTasksData[rawTaskData], kanbanStatus, assignees));
        }
    }

    for (let issue in taskIssues) {
        for (let pullRequest in taskPullRequests) {
            for (let issueUrl in taskPullRequests[pullRequest].issueUrls) {
                if (taskIssues[issue].url === taskPullRequests[pullRequest].issueUrls[issueUrl]) {
                    taskIssues[issue].addPullRequest(taskPullRequests[pullRequest]);
                }
            }
        }
    }
    return taskIssues;
};

/**
 * Parse raw group org project member data fetched from GitHub GraphQL API for visualizing member information and roles
 * @param {Object} rawOrgData raw group org project member data fetched from GitHub GraphQL API
 * @returns {{name, responsibility, imageUrl, url}[]} array of dict objects containing member name, img, links and roles
 */
const getFormattedOrgMemberData = (rawOrgData) => {
    const rawOrgMemberData = rawOrgData['data']['organization']['membersWithRole']['nodes'];
    const formattedOrgMemberData = [];

    for (let memberData in rawOrgMemberData) {
        if (Object.keys(groupAssigneeNames).includes(rawOrgMemberData[memberData]['login'])) {
            formattedOrgMemberData.push({
                'name': groupAssigneeNames[rawOrgMemberData[memberData]['login']],
                'responsibility': groupMemberResponsibilities[groupAssigneeNames[rawOrgMemberData[memberData]['login']]],
                'imageUrl': rawOrgMemberData[memberData]['avatarUrl'],
                'url': rawOrgMemberData[memberData]['url']
            })
        }
    }

    // sort by member surnames
    formattedOrgMemberData.sort((a, b) => {
        let lastNameA = a.name.split(' ').pop();
        let lastNameB = b.name.split(' ').pop();
        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;
        return 0;
    });
    return formattedOrgMemberData;
}

/**
 * Parse raw group organisation data fetched from GitHub GraphQL API for visualizing org name and image information.
 * @param {Object} rawGitHubOrgData raw group organisation data fetched from GitHub GraphQL API
 * @returns {{displayName, imageUrl, linkUrl}} dictionary object containing org display name, image URL, and home URL
 */
const getOrgData = (rawGitHubOrgData) => {
    const orgData = rawGitHubOrgData["data"]["organization"];
    return {
        'displayName': orgData['name'],
        'imageUrl': orgData['avatarUrl'],
        'linkUrl': orgData['url']
    }
}

/**
 * Pagination fetching of all project task data in iterations of up to 100 items from the GitHub GraphQL API
 * @returns {Promise<*[any|[TaskIssue, {name, responsibility, imageUrl, url}[]]]>} array of task and members data
 */
const fetchGitHubOrgProjectData = async () => {
    let cursor = null,
        hasNextPage = true,
        response = null,
        data = null;

    const taskIssues = [];
    while (hasNextPage) {
        response = await fetch(githubGraphQlEndpoint, githubGraphQlQueryOptions(cursor));
        data = await response.json();
        taskIssues.push(...getFormattedGitHubOrgProjectTaskData(data));

        cursor = data['data']['organization']['projectV2']['items']['pageInfo']['endCursor'];
        hasNextPage = data['data']['organization']['projectV2']['items']['pageInfo']['hasNextPage'];
    }

    const orgMemberData = getFormattedOrgMemberData(data); // retrieve org member roles and profile data
    const orgDisplayData = getOrgData(data); // get formatted organisation information data for display

    // sort by names in ascending order (with GROUP prioritized) then task creation in descending order
    taskIssues.sort((a, b) => {
        return a.assignee === 'GROUP' ? -1 : b.assignee === 'GROUP' ? 1 :
            a.assignee.split(' ').pop().localeCompare(b.assignee.split(' ').pop()) || b.number - a.number;
    });
    return [taskIssues, orgDisplayData, orgMemberData];
};

export default fetchGitHubOrgProjectData;
